import { logger } from "@/monitoring/utils";

import {
  API_BASE_URL,
  DEFAULT_API_TIMEOUT,
  DEFAULT_HEADERS,
  GLOBAL_REQUEST_INTERCEPTORS,
  GLOBAL_RESPONSE_INTERCEPTORS,
} from "@/api/api.config";
import getParsedURL from "@/api/utils/getParsedURL";
import getResponseData from "@/api/utils/getResponseData";
import ResponseError from "@/errors/ResponseError";
import stringifyParams from "@/utils/stringifyParams";
import type { NextPageContext } from "next";
export type Interceptor<T> = (input: T) => Promise<T> | T;

export interface QueryParams
  extends Record<string, string | number | null | undefined | boolean> {}

export interface ExtendedRequestInit extends RequestInit {
  timeout?: number;
  ctx?: NextPageContext;
  urlReplacers?: Array<Record<string, unknown>> | Array<Record<string, null>>;
  // grabbed from the type of URLSearchParams
  queryParams?: QueryParams;
  prefixToStrip?: string;
}

export interface RestApiOptions {
  basePath?: string;
  timeout?: number;
  headers?: Headers | Record<string, string> | Array<[string, string]>;
  ctx?: NextPageContext;
  globalRequestInterceptors?: Array<
    Interceptor<{ url: string; config: ExtendedRequestInit }>
  >;
  globalResponseInterceptors?: Array<
    Interceptor<{ response: Response; data: unknown }>
  >;
  refetchOnWindowFocus?: boolean;
}

export class RestApi {
  private readonly basePath: string;
  private readonly defaultTimeout: number;
  private readonly defaultHeaders: Headers;
  private readonly requestInterceptors: Array<
    Interceptor<{ url: string; config: ExtendedRequestInit }>
  >;
  private readonly responseInterceptors: Array<
    Interceptor<{ response: Response; data: unknown }>
  >;
  private readonly controller: AbortController;
  private readonly ctx: NextPageContext | undefined;

  constructor({
    basePath = API_BASE_URL,
    timeout = DEFAULT_API_TIMEOUT,
    headers,
    ctx,
  }: RestApiOptions) {
    this.basePath = basePath;
    this.defaultTimeout = timeout;
    this.ctx = ctx;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.controller = new AbortController();

    this.defaultHeaders = new Headers(DEFAULT_HEADERS);
    const parsedHeaders = new Headers(headers);
    parsedHeaders?.forEach((value, key) => {
      this.defaultHeaders.set(key, value);
    });

    GLOBAL_REQUEST_INTERCEPTORS.forEach((interceptor) => {
      this.addRequestInterceptor(interceptor);
    });

    GLOBAL_RESPONSE_INTERCEPTORS.forEach((interceptor) => {
      this.addResponseInterceptor(interceptor);
    });
  }

  addRequestInterceptor(
    interceptor: Interceptor<{ url: string; config: ExtendedRequestInit }>,
  ) {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  addResponseInterceptor<DataType>(
    interceptor: Interceptor<{ response: Response; data: DataType }>,
  ): RestApi {
    this.responseInterceptors.push(
      interceptor as Interceptor<{ response: Response; data: unknown }>,
    );
    return this;
  }

  getAbortControllerAsCallback(
    abortCallBack: (controller: AbortController) => void,
  ) {
    abortCallBack(this.controller);
    return this;
  }

  /**
   * Returns the AbortController instance to be used in the client side to cancel the request
   * @warning This method isn't chained and requires to keep the reference to the instance in the memory,
   * be careful with it causing memory leaks and racing conditions
   * @example const myWildAPi = api().get('/api'); myWildAPi.getAbortController().abort();
   * @returns {AbortController}
   */
  getAbortController() {
    return this.controller;
  }

  async request<R>(
    endpoint: string,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    let url = getParsedURL(
      `${this.basePath}${endpoint}`,
      options.urlReplacers || [{ id: "me" }],
    );

    const timeout = options.timeout || this.defaultTimeout;
    const timeoutSignal = AbortSignal.timeout(timeout);

    // creates a new Header object cloning the overriders headers sent, doesn't matter the format used
    const headersCopy = new Headers(options.headers);

    // then remove the headers to avoid overriding the default Headers from `this.defaultHeaders`
    delete options.headers;

    let config: ExtendedRequestInit = {
      headers: this.defaultHeaders,
      ...options,
      credentials: "include",
    };

    const parsedHeaders = new Headers(config.headers);

    // then set the new overrider Headers object to the config object back again
    headersCopy.forEach((value, key) => {
      parsedHeaders.set(key, value);
    });

    // set the default cookies from Next.js Context to the request
    if (this.ctx?.req?.headers?.cookie) {
      parsedHeaders?.set("Cookie", this.ctx?.req?.headers?.cookie);
    }

    // Override the default cookies from Next.js if forced by the options
    if (options.ctx?.req?.headers?.cookie) {
      parsedHeaders?.set("Cookie", options.ctx?.req?.headers?.cookie);
    }

    // The browser has to set multipart/form-data with its own boundary, so any
    // Content-Type we default to would leave the body unparseable on the server.
    if (config.body instanceof FormData) parsedHeaders.delete("Content-Type");

    config.headers = parsedHeaders;

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      const result = await interceptor({ url, config });
      url = result.url || url;
      config = result.config || config;
    }

    let response: Response;
    try {
      const queryString = stringifyParams(options.queryParams);
      const requestUrl = `${url}${queryString}`;
      response = await fetch(requestUrl, {
        ...config,
        // @ts-expect-error - AbortSignal not available on current lib.dom.d.ts
        signal: AbortSignal.any([this.controller.signal, timeoutSignal]),
      });
    } catch (error: unknown) {
      const unknownError = error as ResponseError;

      // Log errors to DataDog
      if (unknownError?.name === "AbortError") {
        logger.error({
          message: "AbortError: Request cancelled by the user",
          context: { url, status_code: unknownError.statusCode, options },
          error: unknownError,
        });
      } else if (unknownError?.name === "TimeoutError") {
        logger.error({
          message: "TimeoutError: Request timed out",
          context: { url, status_code: unknownError.statusCode, options },
          error: unknownError,
        });
      } else {
        logger.error({
          message: "Unexpected Error",
          context: { url, status_code: unknownError.statusCode, options },
          error: unknownError,
        });
      }

      throw unknownError;
    }

    if (!response.ok) {
      throw new ResponseError(response, await response.text());
    }

    let data = await getResponseData<R>(response);

    // Apply response interceptors
    for (const interceptor of this.responseInterceptors) {
      const result = await interceptor({ response, data });
      data = (result.data as Awaited<R>) || data;
      response = result.response || response;
    }

    return data;
  }

  async get<R>(
    endpoint: string,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    return await this.request<R>(endpoint, { ...options, method: "GET" });
  }

  async post<R>(
    endpoint: string,
    payload: Record<string, unknown>,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    return await this.request<R>(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async put<R>(
    endpoint: string,
    payload: Record<string, unknown>,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    return await this.request<R>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  async patch<R>(
    endpoint: string,
    payload: Record<string, unknown>,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    return await this.request<R>(endpoint, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async delete<R>(
    endpoint: string,
    options: ExtendedRequestInit = {},
  ): Promise<R> {
    return await this.request<R>(endpoint, { ...options, method: "DELETE" });
  }
}

/**
 * Always create a new REST API instance to be consumed in the Server Side, avoiding the use of a singleton instance,
 * what could cause data leakage between requests.
 * @param {RestApiOptions} options
 */
const api = (options?: RestApiOptions) => new RestApi({ ...options });

export default api;
